import { applyHomeEndKey, isCoarsePointer, shouldSendOnEnter } from "@recruiter/shared";
import { useState } from "react";

import { composeDebugNote, readTesterName, writeTesterName } from "../../lib/debug-note";

// Floating "log a debug note" box, the talent-side twin of the buyer app's
// `DebugNotePanel`. Shown on EVERY view while debug mode is ON. Submit posts the
// note (with an auto-captured screenshot) via `onSubmit`. The box never touches
// the STERLING reply composer. The tester's NAME is required and is folded into
// the note text (see `composeDebugNote`). It is remembered in localStorage under
// the key the buyer app shares.

function localStore(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

type Status =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

export type DebugNotePanelProps = {
  // Posts the composed note text (`name: …\nnote: …`). Resolves on success,
  // throws on failure (surfaced inline).
  onSubmit: (note: string) => Promise<void>;
  // Leave debug mode (mirrors the sidebar pill + the banner's "Turn off").
  onTurnOff: () => void;
};

export function DebugNotePanel({ onSubmit, onTurnOff }: DebugNotePanelProps) {
  const [name, setName] = useState(() => readTesterName(localStore()));
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const saving = status.kind === "saving";
  // Both the name (attribution is the whole point) and the note are required.
  const canSend = name.trim().length > 0 && note.trim().length > 0 && !saving;

  async function send() {
    const who = name.trim();
    const text = note.trim();
    if (!who || !text || saving) return;
    setStatus({ kind: "saving" });
    try {
      await onSubmit(composeDebugNote(who, text));
      // Clear the note but KEEP the name — a tester usually logs several notes.
      setNote("");
      setStatus({ kind: "saved" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not save the note.";
      setStatus({ kind: "error", message });
    }
  }

  function handleKey(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter inserts a newline (a tester may write a few
    // lines). On touch there is no Shift, so Enter always inserts a newline.
    if (shouldSendOnEnter(event, isCoarsePointer())) {
      event.preventDefault();
      void send();
    }
    applyHomeEndKey(event);
  }

  return (
    // `data-html2canvas-ignore`: the box covers part of the page, and the
    // screenshot is of the page, not of the box (Monday 3252698155).
    <div
      className="sl-debug-panel"
      role="region"
      aria-label="Debug note"
      data-html2canvas-ignore="true"
    >
      <div className="sl-debug-panel__head">
        <span className="sl-debug-panel__dot" aria-hidden="true" />
        <span className="sl-debug-panel__title">Debug note</span>
        <button
          type="button"
          className="sl-debug-panel__off"
          onClick={onTurnOff}
          title="Turn off debug mode"
        >
          Turn off
        </button>
      </div>
      <p className="sl-debug-panel__hint">
        Logged for the team, not sent to the concierge. A screenshot of this page is attached.
      </p>
      <input
        className="sl-debug-panel__name"
        type="text"
        aria-label="Your name"
        placeholder="Your name"
        value={name}
        disabled={saving}
        onChange={(event) => {
          setName(event.target.value);
          writeTesterName(localStore(), event.target.value);
          if (status.kind !== "idle" && status.kind !== "saving") setStatus({ kind: "idle" });
        }}
        onKeyDown={applyHomeEndKey}
      />
      <textarea
        className="sl-debug-panel__input"
        aria-label="Describe what's wrong"
        placeholder="Describe what feels wrong on this page…"
        rows={3}
        value={note}
        disabled={saving}
        onChange={(event) => {
          setNote(event.target.value);
          if (status.kind !== "idle" && status.kind !== "saving") setStatus({ kind: "idle" });
        }}
        onKeyDown={handleKey}
      />
      <div className="sl-debug-panel__foot">
        <span
          className={`sl-debug-panel__status sl-debug-panel__status--${status.kind}`}
          role="status"
        >
          {status.kind === "saved" ? "Logged ✓" : status.kind === "error" ? status.message : ""}
        </span>
        <button
          type="button"
          className="sl-btn sl-debug-panel__send"
          disabled={!canSend}
          onClick={() => void send()}
        >
          {saving ? "Logging…" : "Log note"}
        </button>
      </div>
    </div>
  );
}
